import {
  HomeRatesState,
  TasaHome,
  TramoUsdtHome,
} from '../home-rates/models/home-rates.model';
import { PerfilUsuario } from '../remittance-hub/models/remittance.model';
import * as R from '../home-rates/rules/home-rates.rules';

/**
 * Reglas de la "Tasa de hoy" del asistente.
 *
 * El asistente no calcula ni pide tasas propias: toma las filas de la tarjeta
 * "Tasas del día" del home (`HomeRatesService`). Así el número es el mismo en
 * los dos lugares por construcción, y los corredores de cada perfil salen de
 * una sola definición (`home-rates.rules.ts`).
 */

/** Filas listas para pintar, agrupadas igual que la tarjeta del home. */
export interface TasasAsistente {
  /** Riel propio y otros destinos. */
  envios: TasaHome[];
  /** Compra y venta de USDT dentro de la billetera. */
  usdt: TasaHome[];
}

/**
 * `true` cuando el estado ya trae un ciclo terminado del perfil pedido. Un
 * ciclo en curso todavía puede estar llenando filas: se espera a que cierre.
 */
export function cicloListo(st: HomeRatesState, perfil: PerfilUsuario): boolean {
  return st.perfil === perfil && !st.cargando && st.ultimoCicloEn != null;
}

/**
 * Qué filas se publican en el asistente. Una fila vieja o sin número no sale:
 * el home puede mostrarla apagada, pero en una respuesta del asistente un
 * número viejo se lee como la tasa de hoy. `null` si no queda ninguna, para
 * que el asistente caiga a su consulta de respaldo.
 */
export function tasasParaAsistente(st: HomeRatesState): TasasAsistente | null {
  const usable = (t: TasaHome) => !t.vieja && tieneValor(t);
  const envios = [
    ...st.nivel1.filter((t) => t.def.grupo === 'ENVIO'),
    ...st.nivel2,
  ].filter(usable);
  const usdt = st.nivel1.filter((t) => t.def.grupo === 'USDT').filter(usable);
  return envios.length || usdt.length ? { envios, usdt } : null;
}

/**
 * Tramos de VES→USDT de la fila, si los trae (solo el par USDT del cliente
 * VE). Es un bloque aparte: no cambia «Compras a · Vendes a» ni `tieneValor`.
 * `null` = la fila se muestra como siempre.
 */
export function tramosUsdtDe(t: TasaHome): TramoUsdtHome[] | null {
  return t.tramosUsdt && t.tramosUsdt.length ? t.tramosUsdt : null;
}

/** Tramos USDT→VES de la misma fila (espejo). `null` = como siempre. */
export function tramosUsdtVentaDe(t: TasaHome): TramoUsdtHome[] | null {
  return t.tramosUsdtVenta && t.tramosUsdtVenta.length ? t.tramosUsdtVenta : null;
}

function tieneValor(t: TasaHome): boolean {
  if (t.tramos) return t.tramos.length > 0;
  if (R.esPar(t.def)) return t.tasa != null && t.venta != null;
  return t.tasa != null;
}
